import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteOrganisationMedias,
  deleteOrganisationUser,
  getOrganisation,
  getOrganisationInvitations,
  getOrganisationProjects,
  getOrganisationUsers,
  getOrganisations,
  createOrganisation,
  updateOrganisation,
  updateOrganisationMedia,
  updateOrganisationUserRole,
  updateOrganisationVerification,
  createBulkOrganisations,
  deleteOrganisation,
  deleteOrganisations,
  getOrganisationsSpotlight
} from "../organisations/requests";
import type {
  BulkOrganisationPayload,
  CustomQueryOptions,
  Filter,
  Invitation,
  Organisation,
  OrganisationMedia,
  Pagination,
  Project,
  User,
} from "../types";

export const useGetOrganisation = (
  id: string,
  options?: CustomQueryOptions<Organisation>
) => {
  return useQuery({
    queryKey: ["organisations", id],
    queryFn: () => getOrganisation(id),
    ...options,
  });
};

export const useCreateOrganisation = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Organisation>) => createOrganisation(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["organisations"],
        exact: false,
      });
    },
    retry: false,
  });
};

export const useCreateBulkOrganisations = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { organisations: BulkOrganisationPayload[] }) =>
      createBulkOrganisations(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["organisations"],
        exact: false,
      });
    },
  });
};

export const useGetAllOrganisations = (
  filter: Filter,
  options?: CustomQueryOptions<{ data: Organisation[]; pagination: Pagination }>
) => {
  return useQuery({
    queryKey: ["organisations", `all-${JSON.stringify(filter)}`],
    queryFn: () => getOrganisations(filter),
    staleTime: 1000 * 60 * 2,
    ...options,
  });
};

export const useGetOrganisationsSpotlight = (options?: CustomQueryOptions<Organisation>) => {
  return useQuery({
    queryKey: ['organisations', 'spotlight'],
    queryFn: () => getOrganisationsSpotlight(),
    ...options
  });
};

export const useUpdateOrganisationMutation = (
  id: string,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update", "organisations", id],
    mutationFn: (data: Partial<Organisation>) => updateOrganisation(id, data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations", id] });
    },
  });
};

export const useDeleteOrganisationMutation = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete", "organisations"],
    mutationFn: (id: string) => deleteOrganisation(id),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["organisations"],
        exact: false,
      });
    },
  });
};

export const useDeleteOrganisationsMutation = (
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete", "organisations", "multiple"],
    mutationFn: (data: { organisationIds: string[] }) =>
      deleteOrganisations(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["organisations"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["projects"],
        exact: false,
      });
    },
  });
};

export const useUpdateOrganisationVerificationMutation = (
  id: string,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update", "organisations", id, "verified"],
    mutationFn: (data: { verified: boolean }) =>
      updateOrganisationVerification(id, data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations", id] });
    },
  });
};

// Media
export const useUpdateOrganisationMediaMutation = (
  id: string,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update", "organisations", id, "media"],
    mutationFn: (data: { media: OrganisationMedia[] }) =>
      updateOrganisationMedia(id, data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations", id] });
    },
  });
};

export const useDeleteOrganisationMediasMutation = (
  id: string,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete", "organisations", id, "media"],
    mutationFn: (mediaUrls: string[]) =>
      deleteOrganisationMedias(id, mediaUrls),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations", id] });
    },
  });
};

// Projects
export const useGetOrganisationProjects = (
  id: string,
  options?: CustomQueryOptions<Project[]>
) => {
  return useQuery({
    queryKey: ["organisations-projects", id],
    queryFn: () => getOrganisationProjects(id),
    ...options,
  });
};

// Users
export const useGetOrganisationUsers = (
  id: string,
  filter: { page: number; limit: number },
  options?: CustomQueryOptions<{ data: User[]; pagination: Pagination }>
) => {
  return useQuery({
    queryKey: ["organisation-users", id],
    queryFn: () => getOrganisationUsers(id, filter),
    enabled: !!id,
    ...options,
  });
};

export const useUpdateOrganisationUserRole = (
  id: string,
  onSuccess?: () => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { userId: string; role: string }) =>
      updateOrganisationUserRole(id, data.userId, { role: data.role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisation-users", id] });
      onSuccess?.();
    },
    onError,
  });
};

export const useDeleteOrganisationUser = (
  id: string,
  onSuccess?: () => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deleteOrganisationUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisation-users", id] });
      onSuccess?.();
    },
    onError,
  });
};

// Invitations
export const useGetOrganisationInvitations = (
  id: string,
  filter: { page: number; limit: number },
  options?: CustomQueryOptions<{ data: Invitation[]; pagination: Pagination }>
) => {
  return useQuery({
    queryKey: ["organisation-invitations", id],
    queryFn: () => getOrganisationInvitations(id, filter),
    enabled: !!id,
    ...options,
  });
};
